import instrumentCatalog from '../../../socket-bridge/listed-cfd-instruments.json' with { type: 'json' };

// These CFDs are valued in USD by the VPS before the app/database receive them.
export interface ListedCfdInstrument {
  symbol: string;
  name: string;
  type: 'etf' | 'stock';
  active: boolean;
  isin: string;
  sourceSymbol: string;
  mic: string;
  exchange: string;
  exchangeAliases: string[];
  quoteCurrency: 'USD' | 'EUR' | 'CHF' | 'DKK';
  tradingViewSymbol: string;
  aliases: string[];
  lotSize: number;
  quoteMaxAgeMs?: number;
}

export const LISTED_CFD_INSTRUMENTS = instrumentCatalog as ListedCfdInstrument[];

export const getListedCfdInstrument = (symbol: string) =>
  LISTED_CFD_INSTRUMENTS.find(instrument => instrument.symbol === symbol);

export const matchesCfdSearch = (instrument: { symbol: string; name: string }, query: string): boolean => {
  const listing = getListedCfdInstrument(instrument.symbol);
  const searchText = [instrument.symbol, instrument.name, listing?.isin, listing?.exchange, ...(listing?.aliases ?? [])];
  return searchText.some(value => value?.toLowerCase().includes(query.trim().toLowerCase()));
};

// Default limit for current quotes. Execution may have an instrument-specific
// allowance in the catalog (WSRIA: 24 hours); provider times never change.
export const CFD_QUOTE_MAX_AGE_MS = 30 * 60 * 1000;

export const getCfdQuoteMaxAgeMs = (symbol: string): number =>
  getListedCfdInstrument(symbol)?.quoteMaxAgeMs ?? CFD_QUOTE_MAX_AGE_MS;

type CfdQuote = { price: number; timestamp: string } | null | undefined;

const isValidCfdQuote = (quote: CfdQuote, now: number, maxAgeMs: number): boolean => {
  if (!quote || !Number.isFinite(quote.price) || quote.price <= 0) return false;
  const timestamp = Date.parse(quote.timestamp);
  return Number.isFinite(timestamp) && timestamp > 0 && timestamp <= now + 60_000 && now - timestamp <= maxAgeMs;
};

export const isFreshCfdQuote = (quote: CfdQuote, now = Date.now()): boolean =>
  isValidCfdQuote(quote, now, CFD_QUOTE_MAX_AGE_MS);

export const isTradableCfdQuote = (symbol: string, quote: CfdQuote, now = Date.now()): boolean =>
  !!getListedCfdInstrument(symbol) && isValidCfdQuote(quote, now, getCfdQuoteMaxAgeMs(symbol));
